import * as React from 'react';

import PropTypes from 'prop-types';
import * as ramda from 'ramda';
import _ from 'underscore';

import type {FieldProperties} from '../../../../core/schema/schema-types';

import {ColumnPicker} from './column-picker';

const SEARCH_RESULT_BUCKET_NAME = 'Search results';

type Props = {
    entityType: string;
    schema: Array<FieldProperties>;
    selectedColumns: Array<string>;
    onResetFieldsToDefault: () => void;
};

type State = {
    selectedBucket: string;
    selectedColumns: Array<string>;
    searchValue?: string;
    lastSelectedBucket?: string;
};

/*
 * Container component
 */
export class ColumnPickerContainer extends React.PureComponent<Props, State> {
    static propTypes = {
        entityType: PropTypes.string.isRequired,
        schema: PropTypes.array.isRequired,
        selectedColumns: PropTypes.array.isRequired,
        onResetFieldsToDefault: PropTypes.func.isRequired,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedBucket: props.entityType,
            selectedColumns: props.selectedColumns,
        };
    }

    UNSAFE_componentWillReceiveProps(newProps: Props) {
        const previouslySelectedColumns = this.props.selectedColumns;
        const newlySelectedColumns = newProps.selectedColumns;

        if (previouslySelectedColumns !== newlySelectedColumns) {
            this.setState({selectedColumns: newlySelectedColumns});
        }
    }

    render() {
        const {searchValue, selectedColumns} = this.state;

        const columns = this.props.schema
            .filter((column) => isDisplayable(column, selectedColumns))
            .filter((column) => isInSearch(column, searchValue))
            .reduce<Array<any>>(
                (colAccum, column) => mergeSelectedState(colAccum, column, selectedColumns),
                []
            );

        const buckets = bucketColumns(
            columns,
            this.props.entityType,
            Boolean(this.state.searchValue)
        );
        const bucketList = getBucketList(buckets, this.state.selectedBucket);

        return (
            <ColumnPicker
                buckets={buckets}
                bucketList={bucketList}
                onResetFieldsToDefault={this.props.onResetFieldsToDefault}
                searchValue={this.state.searchValue}
                onSearchValueChanged={this.handleSearchValueChanged}
                onFieldSelect={this.handleFieldToggle}
                onBucketSelect={this.handleBucketSelect}
                selectedBucket={this.state.selectedBucket}
            />
        );
    }

    handleSearchValueChanged = (searchValue: string) => {
        this.setState((prevState) => {
            const selectedBucket = searchValue
                ? SEARCH_RESULT_BUCKET_NAME
                : prevState.lastSelectedBucket || this.props.entityType;
            let lastSelectedBucket = prevState.lastSelectedBucket;
            if (searchValue && prevState.selectedBucket !== SEARCH_RESULT_BUCKET_NAME) {
                lastSelectedBucket = prevState.selectedBucket;
            }

            return {searchValue, selectedBucket, lastSelectedBucket};
        });
    };

    handleFieldToggle = (fieldName: string) => {
        if (fieldName === 'name') return;

        this.setState((prevState) => {
            const prevSelectedColumns = prevState.selectedColumns;

            if (prevSelectedColumns.includes(fieldName)) {
                return {selectedColumns: _.without(prevSelectedColumns, fieldName)};
            } else {
                // We want to insert the aiSummary field as close to the top of
                // the list as possible, right after the name field
                if (fieldName === 'aiSummary') {
                    const findIndex = prevSelectedColumns.findIndex((field) => field !== 'name');
                    const insertIndex = findIndex === -1 ? prevSelectedColumns.length : findIndex;

                    const nextSelectedColumns = [...prevSelectedColumns];
                    nextSelectedColumns.splice(insertIndex, 0, fieldName);

                    return {selectedColumns: nextSelectedColumns};
                }

                return {selectedColumns: prevSelectedColumns.concat(fieldName)};
            }
        });
    };

    handleBucketSelect = (bucketName: string) => {
        this.setState({selectedBucket: bucketName});
    };

    getSelectedColumns = () => this.state.selectedColumns;
}

/*
 * Helper functions
 */

type ColumnWithSelected = FieldProperties & {
    selected: boolean;
};

/**
 * Determines if a field is displayable in the column picker:
 * - Columns that are selected will always be displayed
 * - The `name` column will never be displayed
 * - Otherwise, uses the value of `column.isDisplayable`
 * @param {SchemaField} column The column object returned from /fields
 * @param {array} selectedColumns Array of column objects.
 * @returns {boolean} Whether to display the column in the column picker.
 */
function isDisplayable(column: FieldProperties, selectedColumns: Array<string>): boolean {
    return (
        column.name !== 'name' &&
        (column.isDisplayable || ramda.contains(column.name, selectedColumns))
    );
}

function isInSearch(column: FieldProperties, searchValue?: string | null): boolean {
    if (!searchValue) return true;
    const pattern = getCachedSearchPattern(searchValue);

    return pattern.test(column.title);
}

function mergeSelectedState(
    columns: Array<ColumnWithSelected>,
    column: FieldProperties,
    selectedColumns: Array<string>
): Array<ColumnWithSelected> {
    return columns.concat(
        // @ts-expect-error
        ramda.mergeRight(column, {selected: ramda.contains(column.name, selectedColumns)})
    );
}

function bucketColumns(
    columns: Array<ColumnWithSelected>,
    entityType: string,
    isSearching: boolean
): {
    [key: string]: Array<ColumnWithSelected>;
} {
    return ramda.groupBy(
        ({bucket}) => (isSearching ? SEARCH_RESULT_BUCKET_NAME : bucket || entityType),
        columns
    );
}

function getBucketList(
    buckets: {
        [key: string]: Array<ColumnWithSelected>;
    },
    selectedBucket: string
) {
    let bucketList = Object.keys(buckets).map((name) => {
        // @ts-expect-error
        const numSelected = buckets[name].filter(ramda.whereEq({selected: true})).length;

        // @ts-expect-error
        return {name, numSelected, total: buckets[name].length};
    });

    // Always show at least a single list for the selected bucket with 0 items
    if (bucketList.length === 0) {
        // @ts-expect-error
        bucketList = [{name: selectedBucket, count: 0}];
    }

    return bucketList;
}

const getCachedSearchPattern = _.memoize((searchValue: string) => new RegExp(searchValue, 'i'));
